import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../../../Features/inventoryManagementSlice';
import ArrowLeft from '../../../../../Assets/Arrow left.svg';
import Cross from '../../../../../Assets/Modals/closeIcon.svg';
import { CSVContext } from '../../../../../Context/CSVContext';
import AddBlue from '../../../../../Assets/Modals/AddBlue.svg';
import ImageAdd from '../../../../../Assets/Add image.svg';
import { getMediaUrl, loadImage } from '../../../../../lib/utils/utils';
import { v4 as uuidv4 } from 'uuid';
import ImagePreview from './ImagePreview';
import { generateExcelSheet } from '../../../../../lib/utils/apiCalls';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import imageCompression from 'browser-image-compression';
import { toast } from 'react-toastify';
import './toast.css';
import MinusRed from '../../../../../Assets/StorefrontIcons/MinusRed.svg';

function countObjectsWithIdPrefix(id, data) {
    return data.filter(item => item.id.startsWith(id)).length;
}

function sortArrayById(array) {
    return array.sort((a, b) => {
        const aParts = a.id.split('.').map(Number);
        const bParts = b.id.split('.').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;

            if (aPart !== bPart) {
                return aPart - bPart;
            }
        }
        return 0;
    });
}

function findMaxDigitPrefix(data) {
    let maxDigit = -1;

    data.forEach(item => {
        const prefix = item.id.split('.')[0];
        const prefixDigit = parseInt(prefix, 10);

        if (prefixDigit > maxDigit) {
            maxDigit = prefixDigit;
        }
    });

    return maxDigit;
}

function SelectImages() {
    const dispatch = useDispatch();

    const { setDownloadUrl, category, optionList, setOptionList, setCreateCSVPage, productType } = useContext(CSVContext);

    const [buttonAvailable, setButtonAvailable] = useState(false);

    const handleCloseModal = () => {
        dispatch(closeModal());
    }

    const handleBack = () => {
        setCreateCSVPage('category');
    }

    const handleImageSelection = async (e) => {
        const optionId = e.target.id;
        const files = Array.from(e.target.files);

        let images = optionList.find(item => item.id === optionId.split('-')[0]).images;

        if (files.length > 0) {
            if((files.length + images.length) > 5) {
                showToast('Maximum 5 images are allowed.', 'warning');
            }
            else {
                const selectedImages = await Promise.all(files.map(async (file) => {
                    const img = await loadImage(file);

                    return {
                        id: uuidv4(),
                        url: img.src,
                        file: file
                    };
                }));

                const filteredList = selectedImages.filter(image => image !== null && image !== undefined);

                setOptionList(prevOptions => 
                    prevOptions.map(option => 
                        `${option.id}-images` === optionId ? { ...option, images: [ ...option.images, ...filteredList ] } : option
                    )
                );
            }
        }
        e.target.value = null;
    };

    const replaceImage = (newImage) => {
        setOptionList(prevOptions => 
            prevOptions.map(option => 
                ({
                    ...option,
                    images: option.images.map(image => 
                        image.id === newImage.id ? newImage : image
                    )
                })
            )
        );
    }

    const deleteImage = (imageId) => {
        setOptionList(prevOptions => 
            prevOptions.map(option => 
                ({
                    ...option,
                    images: option.images.filter(image => 
                        image.id !== imageId
                    )
                })
            )
        );
    }

    const handleAddProduct = () => {
        const maxDigit = findMaxDigitPrefix(optionList);
        let id;
        if(optionList.length === 0) {
            id = 1;
        }
        else {
            id = `${maxDigit+1}`;
        }
        setOptionList([ ...optionList, { id: id, type: 'product', images: [] } ]);
    }

    const handleAddVariant = (productListId) => {
        let count = countObjectsWithIdPrefix(productListId, optionList)
        let id = `${productListId}.${count}`;

        setOptionList([ ...optionList, { id: id, type: 'variant', images: [] } ]);
    }
    
    function handleRemoveOption(option) {
        setOptionList(prev => {
        const next = [...prev];

        if (option.type === "product") {
            const prefix = option.id + ".";
            return next.filter(o => !(o.id === option.id || o.id.startsWith(prefix)));
        }

        if (option.type === "variant") {
            const [base] = option.id.split(".");
            const variants = next
                .filter(o => o.id.startsWith(base + "."))
                .sort((a, b) => parseFloat(a.id) - parseFloat(b.id));

            const filtered = variants.filter(v => v.id !== option.id);

            const reassigned = filtered.map((v, i) => ({
                ...v,
                id: `${base}.${i + 1}`,
            }));

            const remaining = next.filter(o => !o.id.startsWith(base + "."));
            return [...remaining, ...reassigned].sort((a, b) => parseFloat(a.id) - parseFloat(b.id));
        }

        return next;
        });
        }

    const handleGenerate = async () => {
        optionList.forEach(option => {
            if (option.images.length === 0) {
                const name = option.type === 'product' ? `Product ${option.id}` : `Product ${option.id.split('.')[0]} Variant ${option.id.split('.')[1]}`
                showToast(`Images not available for ${name}.`, 'warning');
            }
        });

        if (buttonAvailable) {
            let dataList = [];

            const processOptions = new Promise(async (resolve, reject) => {
                try {
                    for (const option of optionList) {
                        let images = [];
                        let compressedImageList = [];

                        try {
                            const imagePromises = option.images.map(async image => {
                                if (image.file.size > 1048576) {
                                    try {
                                        let compressedBlob = await imageCompression(image.file, {
                                            maxSizeMB: 1,
                                            maxWidthOrHeight: 1920,
                                            useWebWorker: true
                                        });

                                        let compressedFile = new File([compressedBlob], image.file.name, { type: image.file.type, lastModified: Date.now() });

                                        if (compressedFile) {
                                            const img = await loadImage(compressedFile);

                                            return {
                                                id: uuidv4(),
                                                url: img.src,
                                                file: compressedFile
                                            };
                                        }
                                    }
                                    catch (err) {
                                        throw err;
                                    }
                                }
                                else {
                                    return {
                                        id: uuidv4(),
                                        url: URL.createObjectURL(image.file),
                                        file: image.file,
                                    }
                                }
                            })

                            const selectedImages = await Promise.all(imagePromises);
                            compressedImageList = [...selectedImages];
                        }
                        catch (err) {
                            throw err;
                        }

                        for (const image of compressedImageList) {
                            let url = await getMediaUrl(image.file);
                            images.push(url);
                        }

                        let dataObject = {
                            id: option.id,
                            imageUrls: images,
                        }

                        dataList.push(dataObject);
                    }

                    resolve(dataList);
                }
                catch (err) {
                    reject(err);
                }
            });

            toast.promise(
                processOptions,
                {
                    pending: {
                        render() {
                            return 'Processing images and generating data...';
                        },
                        className: 'toast-pending',
                    },
                    error: {
                        render() {
                            return 'Failed to process images. Please try again.';
                        },
                        className: 'toast-error',
                    },
                    success: {
                        render() {
                            return 'Images processed successfully!';
                        },
                    }
                }
            );

            try {
                const dataList = await processOptions;

                let callData = {
                    productCategory: category,
                    productType: productType,
                    data: sortArrayById(dataList)
                }

                const response = await generateExcelSheet(callData);
                if(response?.status === 200) {
                    const url = window.URL.createObjectURL(response.data);
                    setDownloadUrl(url);
                    setCreateCSVPage('download');
                }
                else {
                    showToast('Error generating Excel sheet. Please try again.', 'error');
                }
            }
            catch (err) {
                showToast('Error generating Excel sheet. Please try again.', 'error');
            }
        }
    }

    useEffect(() => {
        let error = false;

        optionList.forEach(option => {
            if (option.images.length === 0) {
                error = true;
            }
        });

        if(!error) {
            setButtonAvailable(true);
        }
        else {
            setButtonAvailable(false);
        }
    }, [optionList]);

    return (
        <div className='w-[44%] bg-[#FFFFFF] border border-[#EAEAEA] rounded-[9px] p-4 flex flex-col gap-4'>
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={handleBack} className="size-8 outline-none flex items-center justify-center">
                            <img src={ArrowLeft} alt="" />
                        </button>

                        <h1 className='font-semibold text-2xl text-[#121212]'>Upload Product Images</h1>
                    </div>

                    <button onClick={handleCloseModal} className="w-11 h-11 flex items-center justify-center">
                        <img src={Cross} alt="" />
                    </button>
                </div>

                <div className="flex flex-col gap-3 max-h-80 overflow-y-scroll">
                    {optionList.map(option => {
                        const uniqueId = `${option.id}-images`;

                        return (
                            <div key={option.id} className="flex flex-col gap-[6px]">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => handleRemoveOption(option)} className='text-[14px] leading-5 text-[#DC2626] select-none'>
                                            <img className='size-5 flex items-center justify-center' src={MinusRed} alt="" />
                                        </button>

                                        <p className='font-medium text-[16px] leading-7 text-[#666666]'>
                                            {option.type === 'product' ? `Product ${option.id}` : `Product ${option.id.split('.')[0]} Variant ${option.id.split('.')[1]}`}
                                        </p>
                                    </div>

                                    {
                                        option.type === 'product' && 
                                        <button onClick={() => handleAddVariant(option.id)} className='w-fit flex items-center'>
                                            <div className="w-6 h-6 flex items-center justify-center">
                                                <img src={AddBlue} alt="" />
                                            </div>

                                            <p className='font-medium text-base text-[#1055EB]'>Add variant</p>
                                        </button>
                                    }
                                </div>

                                <div key={option.id} className="flex flex-wrap gap-3">
                                    {
                                        option.images.length < 5 && 
                                        <div className="flex flex-col gap-2">
                                            <label htmlFor={uniqueId} className='cursor-pointer size-[88px] flex items-center justify-center border-[3px] border-[#EAEAEA] rounded-xl border-dashed'>
                                                <img src={ImageAdd} alt="" />
                                            </label>

                                            <input 
                                                className='hidden' 
                                                type="file" 
                                                accept="image/*" 
                                                name={uniqueId} 
                                                id={uniqueId} 
                                                multiple
                                                onChange={handleImageSelection} 
                                            />

                                            <p className='font-medium text-[14px] leading-[22px] text-[#666666]'>*Max 5 images</p>
                                        </div>
                                    }

                                    {option.images.map(image => {
                                        return (
                                            <ImagePreview 
                                                key={image.id} 
                                                image={image} 
                                                handleReplace={replaceImage} 
                                                handleDelete={deleteImage} 
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button onClick={handleAddProduct} className='w-fit flex items-center'>
                    <div className="w-6 h-6 flex items-center justify-center">
                        <img src={AddBlue} alt="" />
                    </div>

                    <p className='font-medium text-base text-[#1055EB]'>Add product</p>
                </button>
            </div>

            <button onClick={handleGenerate} className={`px-4 py-2 ${buttonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg cursor-not-allowed'} text-buttonText rounded font-semibold text-base`}>Generate Catalog File</button>
        </div>
    );
}

export default SelectImages;
